import { LitElement, html } from "lit";
import { customElement } from "lit/decorators.js";
import { apiBaseUrl } from "../constants";
import {
  fetchApi,
  pushAnalyticsPackageInteraction,
  nugetIdToUrlAlias,
  AppClassToggle,
  setPageMetaTitle,
  setPageMetaDescription,
} from "../functions";
import { StateController } from "../state";
import type { OpenModalEvent } from "../events";
import { removeImageMeta } from "../functions/set-page-metadata";
import type { UmbMarketPackageModal } from "./package/UmbMarketPackageModal";
import type { Package } from "./package/entities";

export interface MetaItem {
  title: string;
  description: string;
  packageId?: string;
  direct?: boolean;
  previousUrl?: string;
}

@customElement("umb-market-modal-container")
export class UmbMarketModalContainer extends LitElement {
  #metaStack: Array<MetaItem> = [];

  connectedCallback() {
    document.addEventListener("open-modal", this.open);
    document.addEventListener("close-modal", this.close);
  }

  disconnectedCallback(): void {
    document.removeEventListener("open-modal", this.open);
    document.removeEventListener("close-modal", this.close);
  }

  #createModalElement(iconDominantColor?: string) {
    const modal = document.createElement(
      "umb-market-package-modal"
    ) as UmbMarketPackageModal;

    modal.style.setProperty(
      "--dominant-color-header",
      `${iconDominantColor ? `${iconDominantColor}e6` : "transparent"}`
    );

    // use id for closing correct modal on esc press
    modal.id = new Date().getTime().toString();

    this.appendChild(modal);

    return modal;
  }

  #truncateText(text?: string, maxLength = 160) {
    if (!text) return null;

    if (text.length <= maxLength) {
      return text;
    }

    const truncated = text.slice(0, maxLength);
    const lastFullStopIndex = truncated.lastIndexOf(".");

    if (lastFullStopIndex === -1) {
      // No full stop before the cutoff — return the truncated text as-is
      return truncated;
    }

    return truncated.slice(0, lastFullStopIndex + 1);
  }

  /**
   *
   * @param e
   * @returns
   */
  open = async (e: Event) => {
    const { detail } = e as CustomEvent<OpenModalEvent>;
    const modal = this.#createModalElement(detail.iconDominantColor);
    // search is not available on router, plus is lost somewhere between here and history.pushState below
    // so instead, we'll store is in a variable for later
    const historyUrl = window.location.pathname + window.location.search;

    setTimeout(() => {
      AppClassToggle("overlay-in", "add");
      modal.classList.add("overlay-in");
    }, 1);

    let url = `${apiBaseUrl()}packages/${detail.packageId}`;
    // if an umb version selected, pass this as well
    if (StateController.current.filter?.supportsUmbracoVersionNumber) {
      url += `?supportsUmbracoVersionNumber=${StateController.current.filter.supportsUmbracoVersionNumber}`;
    }

    const packageData = await fetchApi<Package>(url);

    if (!packageData) {
      return;
    }

    // add history entry for the page triggering the modal
    if (!detail.direct) {
      window.history.pushState({}, "", historyUrl);
    }

    // title and description are not the updated page - these track
    // the values to restore on close
    this.#metaStack.push({
      title: document.title,
      description:
        document
          .querySelector('meta[name="description"]')
          ?.getAttribute("content") ?? "",
      packageId: packageData.packageId,
      direct: detail.direct,
      previousUrl: historyUrl,
    });

    // update meta fields with new package details
    // this is after the history push state as the history should be the previous view,
    // then the updated title and description are available to replaceState below
    setPageMetaTitle(`${packageData.title} | Umbraco Marketplace`);

    const metaDescription =
      this.#truncateText(packageData.description) ??
      `${packageData.title} is listed on the official Umbraco Marketplace | Information, links, and install instructions`;

    setPageMetaDescription(metaDescription);

    // next, update the history stack with the full url for the package view
    // this will show in the URL bar, but not in the history stack until we go back
    window.history.replaceState({}, "", this.#packagePath(detail.packageId));

    modal.open(packageData);

    // push the GTM event into dataLayer as the package modal
    // does not trigger router events, so does not raise
    // the vaadin-router-location-changed event
    pushAnalyticsPackageInteraction(packageData);
  };

  closeAll() {
    const activeModals = Array.from(
      this.querySelectorAll("umb-market-package-modal")
    );
    if (!activeModals.length) {
      return;
    }

    // always remove image meta as this is set only for direct package entries
    // and should not exist once the modal is dismissed
    removeImageMeta();

    const closingMeta = this.#metaStack[0];
    if (!closingMeta) {
      return;
    }

    setPageMetaTitle(closingMeta.title);
    setPageMetaDescription(closingMeta.description);

    activeModals.forEach((modal: Element) => {
      const m = modal as UmbMarketPackageModal;
      m.classList.remove("overlay-in");
      m.visible = false;
    });

    setTimeout(() => {
      activeModals.forEach((modal: Element) => {
        modal.remove();
      });
      AppClassToggle("overlay-in", "remove");
    }, 500);
  }

  /**
   *
   * @param e
   * @returns
   */
  close = (e: Event) => {
    const { detail } = e as CustomEvent;
    // should always be last, but check anyway
    const closingMetaIndex = this.#metaStack.findIndex(
      (x) => x.packageId === detail.packageId
    );

    if (closingMetaIndex < 0) {
      return;
    }

    const closingMeta = this.#metaStack[closingMetaIndex];

    if (!closingMeta.direct) {
      window.history.pushState(
        {},
        "",
        this.#packagePath(closingMeta.packageId!)
      );
    }

    setPageMetaTitle(closingMeta.title);
    setPageMetaDescription(closingMeta.description);
    removeImageMeta();

    if (closingMetaIndex !== 0) {
      const previousModal = this.#metaStack[closingMetaIndex - 1];
      window.history.replaceState(
        {},
        "",
        this.#packagePath(previousModal.packageId!)
      );
    } else {
      window.history.replaceState(
        {},
        "",
        closingMeta.direct ? "/" : closingMeta.previousUrl
      );
    }

    this.#metaStack = this.#metaStack.filter(
      (x) => x.packageId !== detail.packageId
    );
  };

  // eslint-disable-next-line class-methods-use-this
  #packagePath(packageId: string) {
    return `/package/${nugetIdToUrlAlias(packageId)}`;
  }

  render() {
    return html``;
  }
}
